import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

// ─── Constants ───────────────────────────────────────────────────────────────

const API_BASE = 'https://klorah-fast-server-my8l.onrender.com/api';

/** Trust tier definitions */
export const TRUST_TIERS = {
  TIER_0: {
    id: 0,
    name: 'New Account',
    payoutDelayDays: 7,
    reservePercent: 20,
    reserveDurationDays: 30,
    manualReviewThreshold: 1000, // manual review on payments > $1000
  },
  TIER_1: {
    id: 1,
    name: 'Verified Activity',
    payoutDelayDays: 3,
    reservePercent: 10,
    reserveDurationDays: 14,
    manualReviewThreshold: 5000,
  },
  TIER_2: {
    id: 2,
    name: 'Trusted Creator',
    payoutDelayDays: 1,
    reservePercent: 0,
    reserveDurationDays: 0,
    manualReviewThreshold: null, // no manual review needed
  },
};

/** Risk level thresholds */
export const RISK_LEVELS = {
  LOW: { label: 'Low', threshold: 30, color: 'green' },
  MEDIUM: { label: 'Medium', threshold: 60, color: 'yellow' },
  HIGH: { label: 'High', threshold: 100, color: 'red' },
};

/** Fraud flag trigger conditions */
export const FRAUD_FLAG_TYPES = {
  LARGE_FIRST_PAYMENT: 'large_first_payment',
  VELOCITY_SPIKE: 'velocity_spike',
  IP_MISMATCH: 'ip_mismatch',
  DISPUTE_BEFORE_PAYOUT: 'dispute_before_payout',
  RAPID_PAYOUT_REQUEST: 'rapid_payout_request',
  DISPOSABLE_EMAIL: 'disposable_email',
  AMOUNT_ANOMALY: 'amount_anomaly',
};

/** Payout clearance states */
export const CLEARANCE_STATUS = {
  PENDING: 'pending_clearance',
  UNDER_REVIEW: 'under_review',
  CLEARED: 'cleared',
  FROZEN: 'frozen',
  CANCELLED: 'cancelled',
};

// ─── Disposable email domains (subset for client-side check) ────────────────

const DISPOSABLE_EMAIL_DOMAINS = new Set([
  'tempmail.com', 'throwaway.email', 'guerrillamail.com', 'mailinator.com',
  'yopmail.com', 'sharklasers.com', 'guerrillamailblock.com', 'grr.la',
  'dispostable.com', 'trashmail.com', 'temp-mail.org', 'fakeinbox.com',
  'tempinbox.com', 'jetable.org', 'trash-mail.com', 'getairmail.com',
  'getnada.com', 'emailondeck.com', 'tempr.email', '10minutemail.com',
]);

// ─── Risk Scoring Engine ────────────────────────────────────────────────────

export class RiskScoringEngine {
  /**
   * Calculate a risk score (0-100) for a payment + account combination.
   *
   * @param {Object} params
   * @param {Object} params.account        - Connected account data from Firestore
   * @param {Object} params.payment        - Payment / invoice data
   * @param {Object} [params.radarScore]   - Stripe Radar risk score (0-100) if available
   * @returns {{ score: number, level: string, factors: Array }}
   */
  static evaluate({ account, payment, radarScore = null }) {
    let score = 0;
    const factors = [];

    // 1. Account age (0-20 pts)
    const accountAgeDays = account.createdAt
      ? Math.floor((Date.now() - account.createdAt.toDate().getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    if (accountAgeDays < 7) {
      score += 20;
      factors.push({ factor: 'Account age < 7 days', points: 20 });
    } else if (accountAgeDays < 30) {
      score += 10;
      factors.push({ factor: 'Account age < 30 days', points: 10 });
    }

    // 2. First-time payout (0-15 pts)
    if (!account.firstPayoutCompletedAt) {
      score += 15;
      factors.push({ factor: 'No previous payouts', points: 15 });
    }

    // 3. Payment amount vs historical average (0-20 pts)
    const paymentAmount = parseFloat(payment.amount || payment.total || 0);
    const avgPayment = account.avgPaymentAmount || 0;

    if (avgPayment > 0 && paymentAmount > avgPayment * 3) {
      score += 20;
      factors.push({ factor: `Amount ${paymentAmount} is >3x average (${avgPayment.toFixed(2)})`, points: 20 });
    } else if (avgPayment > 0 && paymentAmount > avgPayment * 2) {
      score += 10;
      factors.push({ factor: `Amount ${paymentAmount} is >2x average (${avgPayment.toFixed(2)})`, points: 10 });
    } else if (avgPayment === 0 && paymentAmount > 1000) {
      score += 15;
      factors.push({ factor: `Large first payment ($${paymentAmount})`, points: 15 });
    }

    // 4. Email domain check (0-10 pts)
    const email = account.email || '';
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain && DISPOSABLE_EMAIL_DOMAINS.has(emailDomain)) {
      score += 10;
      factors.push({ factor: 'Disposable email domain', points: 10 });
    }

    // 5. Stripe Radar score if available (0-20 pts)
    if (radarScore !== null) {
      if (radarScore >= 75) {
        score += 20;
        factors.push({ factor: `Stripe Radar high risk (${radarScore})`, points: 20 });
      } else if (radarScore >= 50) {
        score += 10;
        factors.push({ factor: `Stripe Radar elevated risk (${radarScore})`, points: 10 });
      }
    }

    // 6. Velocity check (0-15 pts)
    const recentPaymentCount = account.recentPaymentCount || 0;
    if (recentPaymentCount > 5) {
      score += 15;
      factors.push({ factor: `${recentPaymentCount} payments in last 24h`, points: 15 });
    } else if (recentPaymentCount > 3) {
      score += 8;
      factors.push({ factor: `${recentPaymentCount} payments in last 24h`, points: 8 });
    }

    // 7. Identity verification status (0-10 pts)
    if (!account.identityVerified) {
      score += 5;
      factors.push({ factor: 'Identity not verified', points: 5 });
    }

    // 8. Dispute history (0-15 pts)
    const disputeCount = account.disputeCount || 0;
    if (disputeCount > 0) {
      const disputePoints = Math.min(disputeCount * 5, 15);
      score += disputePoints;
      factors.push({ factor: `${disputeCount} previous dispute(s)`, points: disputePoints });
    }

    // Clamp to 0-100
    score = Math.min(Math.max(score, 0), 100);

    // Determine risk level
    let level = 'LOW';
    if (score >= RISK_LEVELS.HIGH.threshold - 40) level = 'HIGH';
    else if (score >= RISK_LEVELS.MEDIUM.threshold - 30) level = 'MEDIUM';

    return { score, level, factors };
  }
}

// ─── Fraud Prevention Service ──────────────────────────────────────────────

export class FraudPreventionService {
  constructor() {
    this.firestore = getFirestore();
  }

  // ── Account Risk Profile ──────────────────────────────────────────────

  /**
   * Get or create a risk profile for a connected account.
   */
  async getAccountRiskProfile(userId) {
    const ref = doc(this.firestore, 'riskProfiles', userId);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      return { id: snap.id, ...snap.data() };
    }

    // Create default profile
    const defaultProfile = {
      userId,
      trustTier: 0,
      riskScore: 0,
      riskLevel: 'LOW',
      totalPayments: 0,
      totalPayoutAmount: 0,
      avgPaymentAmount: 0,
      disputeCount: 0,
      firstPayoutCompletedAt: null,
      identityVerified: false,
      accountFrozen: false,
      frozenReason: null,
      reserveBalance: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(ref, defaultProfile);
    return { id: userId, ...defaultProfile };
  }

  /**
   * Update the risk profile after a payment is received.
   */
  async updateRiskProfileAfterPayment(userId, paymentAmount) {
    const profile = await this.getAccountRiskProfile(userId);
    const newTotal = profile.totalPayments + 1;
    const newAvg =
      (profile.avgPaymentAmount * profile.totalPayments + paymentAmount) / newTotal;

    const ref = doc(this.firestore, 'riskProfiles', userId);
    await updateDoc(ref, {
      totalPayments: newTotal,
      avgPaymentAmount: newAvg,
      updatedAt: serverTimestamp(),
    });

    return { ...profile, totalPayments: newTotal, avgPaymentAmount: newAvg };
  }

  // ── Risk Evaluation ───────────────────────────────────────────────────

  /**
   * Evaluate risk for an incoming payment and store the result.
   */
  async evaluatePaymentRisk(userId, paymentData) {
    const profile = await this.getAccountRiskProfile(userId);

    // Get user account data for age
    const userDoc = await getDoc(doc(this.firestore, 'users', userId));
    const userData = userDoc.exists() ? userDoc.data() : {};

    const account = {
      ...profile,
      email: userData.email || '',
      createdAt: userData.createdAt || profile.createdAt,
    };

    const result = RiskScoringEngine.evaluate({
      account,
      payment: paymentData,
    });

    // Store the evaluation
    const evaluationRef = collection(this.firestore, 'riskEvaluations');
    const evaluation = {
      userId,
      invoiceId: paymentData.invoiceId || null,
      paymentAmount: parseFloat(paymentData.amount || paymentData.total || 0),
      riskScore: result.score,
      riskLevel: result.level,
      factors: result.factors,
      createdAt: serverTimestamp(),
    };

    await addDoc(evaluationRef, evaluation);

    // Update the risk profile with latest score
    const profileRef = doc(this.firestore, 'riskProfiles', userId);
    await updateDoc(profileRef, {
      riskScore: result.score,
      riskLevel: result.level,
      updatedAt: serverTimestamp(),
    });

    return result;
  }

  // ── Payout Clearance ──────────────────────────────────────────────────

  /**
   * Create a payout clearance record after a payment is captured.
   * Funds remain in "pending clearance" until the delay passes and risk is acceptable.
   */
  async createPayoutClearance(userId, invoiceId, paymentAmount) {
    const profile = await this.getAccountRiskProfile(userId);
    const tierConfig = Object.values(TRUST_TIERS).find(t => t.id === profile.trustTier)
      || TRUST_TIERS.TIER_0;

    const delayMs = tierConfig.payoutDelayDays * 24 * 60 * 60 * 1000;
    const eligibleAt = new Date(Date.now() + delayMs);

    // Calculate reserve amount
    const reserveAmount = (paymentAmount * tierConfig.reservePercent) / 100;
    const payoutAmount = paymentAmount - reserveAmount;

    const clearance = {
      userId,
      invoiceId,
      paymentAmount,
      payoutAmount,
      reserveAmount,
      reservePercent: tierConfig.reservePercent,
      trustTier: profile.trustTier,
      payoutDelayDays: tierConfig.payoutDelayDays,
      status: CLEARANCE_STATUS.PENDING,
      riskScore: profile.riskScore,
      riskLevel: profile.riskLevel,
      createdAt: serverTimestamp(),
      eligibleAt: Timestamp.fromDate(eligibleAt),
      releasedAt: null,
      frozenAt: null,
      frozenReason: null,
    };

    // Check if manual review is needed
    if (tierConfig.manualReviewThreshold && paymentAmount >= tierConfig.manualReviewThreshold) {
      clearance.status = CLEARANCE_STATUS.UNDER_REVIEW;
      clearance.requiresManualReview = true;
    }

    // If profile is frozen, freeze the clearance too
    if (profile.accountFrozen) {
      clearance.status = CLEARANCE_STATUS.FROZEN;
      clearance.frozenAt = serverTimestamp();
      clearance.frozenReason = 'Account frozen';
    }

    const ref = collection(this.firestore, 'payoutClearances');
    const docRef = await addDoc(ref, clearance);

    return { id: docRef.id, ...clearance };
  }

  /**
   * Get all payout clearances for a user.
   */
  async getPayoutClearances(userId) {
    const q = query(
      collection(this.firestore, 'payoutClearances'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Get all pending clearances across all users (for admin dashboard).
   */
  async getPendingClearances() {
    const q = query(
      collection(this.firestore, 'payoutClearances'),
      where('status', 'in', [CLEARANCE_STATUS.PENDING, CLEARANCE_STATUS.UNDER_REVIEW]),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Release a payout clearance — mark as cleared.
   */
  async releaseClearance(clearanceId) {
    const ref = doc(this.firestore, 'payoutClearances', clearanceId);
    await updateDoc(ref, {
      status: CLEARANCE_STATUS.CLEARED,
      releasedAt: serverTimestamp(),
    });
  }

  /**
   * Freeze a payout clearance.
   */
  async freezeClearance(clearanceId, reason) {
    const ref = doc(this.firestore, 'payoutClearances', clearanceId);
    await updateDoc(ref, {
      status: CLEARANCE_STATUS.FROZEN,
      frozenAt: serverTimestamp(),
      frozenReason: reason,
    });
  }

  /**
   * Cancel a payout clearance (e.g., when a refund is issued during delay).
   */
  async cancelClearance(clearanceId, reason) {
    const ref = doc(this.firestore, 'payoutClearances', clearanceId);
    await updateDoc(ref, {
      status: CLEARANCE_STATUS.CANCELLED,
      cancelledAt: serverTimestamp(),
      cancelReason: reason,
    });
  }

  // ── Fraud Flags ──────────────────────────────────────────────────────

  /**
   * Check and create automatic fraud flags based on trigger conditions.
   */
  async checkAndCreateFraudFlags(userId, paymentData) {
    const profile = await this.getAccountRiskProfile(userId);
    const flags = [];
    const paymentAmount = parseFloat(paymentData.amount || paymentData.total || 0);

    // 1. First payment > $1,000
    if (profile.totalPayments <= 1 && paymentAmount > 1000) {
      flags.push({
        type: FRAUD_FLAG_TYPES.LARGE_FIRST_PAYMENT,
        severity: 'high',
        description: `First payment of $${paymentAmount.toFixed(2)} exceeds $1,000 threshold`,
      });
    }

    // 2. Velocity check — multiple payments in < 24 hours
    const recentPayments = await this.getRecentPayments(userId, 24);
    if (recentPayments.length > 3) {
      flags.push({
        type: FRAUD_FLAG_TYPES.VELOCITY_SPIKE,
        severity: recentPayments.length > 5 ? 'high' : 'medium',
        description: `${recentPayments.length} payments received in the last 24 hours`,
      });
    }

    // 3. Amount anomaly — >3x average
    if (profile.avgPaymentAmount > 0 && paymentAmount > profile.avgPaymentAmount * 3) {
      flags.push({
        type: FRAUD_FLAG_TYPES.AMOUNT_ANOMALY,
        severity: 'medium',
        description: `Payment $${paymentAmount.toFixed(2)} is >3x the average of $${profile.avgPaymentAmount.toFixed(2)}`,
      });
    }

    // 4. Disposable email
    const userDoc = await getDoc(doc(this.firestore, 'users', userId));
    const email = userDoc.exists() ? userDoc.data().email || '' : '';
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain && DISPOSABLE_EMAIL_DOMAINS.has(emailDomain)) {
      flags.push({
        type: FRAUD_FLAG_TYPES.DISPOSABLE_EMAIL,
        severity: 'medium',
        description: `Account uses disposable email domain (${emailDomain})`,
      });
    }

    // Store flags
    for (const flag of flags) {
      await addDoc(collection(this.firestore, 'fraudFlags'), {
        userId,
        invoiceId: paymentData.invoiceId || null,
        ...flag,
        status: 'active',
        createdAt: serverTimestamp(),
        resolvedAt: null,
        resolvedBy: null,
      });
    }

    // If any high-severity flags, freeze the account
    const hasHighSeverity = flags.some(f => f.severity === 'high');
    if (hasHighSeverity) {
      await this.freezeAccount(userId, 'Automated fraud flag triggered');
    }

    return flags;
  }

  /**
   * Get active fraud flags for a user.
   */
  async getFraudFlags(userId) {
    const q = query(
      collection(this.firestore, 'fraudFlags'),
      where('userId', '==', userId),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Get all active fraud flags (for admin dashboard).
   */
  async getAllActiveFraudFlags() {
    const q = query(
      collection(this.firestore, 'fraudFlags'),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Resolve a fraud flag.
   */
  async resolveFraudFlag(flagId, resolvedBy, resolution) {
    const ref = doc(this.firestore, 'fraudFlags', flagId);
    await updateDoc(ref, {
      status: 'resolved',
      resolvedAt: serverTimestamp(),
      resolvedBy,
      resolution,
    });
  }

  // ── Account Freeze / Unfreeze ─────────────────────────────────────────

  /**
   * Freeze an account's payouts.
   */
  async freezeAccount(userId, reason) {
    const ref = doc(this.firestore, 'riskProfiles', userId);
    await updateDoc(ref, {
      accountFrozen: true,
      frozenReason: reason,
      frozenAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Also freeze any pending clearances
    const pendingClearances = await this.getPayoutClearances(userId);
    for (const clearance of pendingClearances) {
      if (clearance.status === CLEARANCE_STATUS.PENDING || clearance.status === CLEARANCE_STATUS.UNDER_REVIEW) {
        await this.freezeClearance(clearance.id, reason);
      }
    }
  }

  /**
   * Unfreeze an account's payouts.
   */
  async unfreezeAccount(userId) {
    const ref = doc(this.firestore, 'riskProfiles', userId);
    await updateDoc(ref, {
      accountFrozen: false,
      frozenReason: null,
      frozenAt: null,
      updatedAt: serverTimestamp(),
    });
  }

  // ── Trust Tier Management ─────────────────────────────────────────────

  /**
   * Evaluate and potentially upgrade a user's trust tier.
   */
  async evaluateTrustTier(userId) {
    const profile = await this.getAccountRiskProfile(userId);
    const userDoc = await getDoc(doc(this.firestore, 'users', userId));
    const userData = userDoc.exists() ? userDoc.data() : {};

    const accountAgeDays = userData.createdAt
      ? Math.floor((Date.now() - userData.createdAt.toDate().getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    let newTier = 0;

    // Tier 1 requirements: 30+ days old, no disputes, 3+ successful payments, identity verified
    if (
      accountAgeDays >= 30 &&
      profile.disputeCount === 0 &&
      profile.totalPayments >= 3 &&
      !profile.accountFrozen
    ) {
      newTier = 1;
    }

    // Tier 2 requirements: 90+ days old, no disputes, 10+ successful payments, identity verified
    if (
      accountAgeDays >= 90 &&
      profile.disputeCount === 0 &&
      profile.totalPayments >= 10 &&
      profile.identityVerified &&
      !profile.accountFrozen
    ) {
      newTier = 2;
    }

    // Only upgrade, never downgrade automatically (manual review for downgrades)
    if (newTier > profile.trustTier) {
      const ref = doc(this.firestore, 'riskProfiles', userId);
      await updateDoc(ref, {
        trustTier: newTier,
        tierUpgradedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return newTier;
    }

    return profile.trustTier;
  }

  /**
   * Manually set a user's trust tier (admin action).
   */
  async setTrustTier(userId, tier, setBy) {
    const ref = doc(this.firestore, 'riskProfiles', userId);
    await updateDoc(ref, {
      trustTier: tier,
      tierSetBy: setBy,
      tierSetAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  // ── Dispute Handling ──────────────────────────────────────────────────

  /**
   * Handle a dispute event from Stripe webhook.
   */
  async handleDisputeCreated(userId, disputeData) {
    // Increment dispute count
    const profileRef = doc(this.firestore, 'riskProfiles', userId);
    const profile = await this.getAccountRiskProfile(userId);

    await updateDoc(profileRef, {
      disputeCount: (profile.disputeCount || 0) + 1,
      lastDisputeAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Find and freeze any pending clearance for this payment
    if (disputeData.invoiceId) {
      const q = query(
        collection(this.firestore, 'payoutClearances'),
        where('invoiceId', '==', disputeData.invoiceId),
        where('status', 'in', [CLEARANCE_STATUS.PENDING, CLEARANCE_STATUS.UNDER_REVIEW])
      );
      const snap = await getDocs(q);
      for (const d of snap.docs) {
        await this.freezeClearance(d.id, 'Dispute filed');
      }
    }

    // Freeze the account
    await this.freezeAccount(userId, 'Dispute filed on payment');

    // Create a fraud flag
    await addDoc(collection(this.firestore, 'fraudFlags'), {
      userId,
      invoiceId: disputeData.invoiceId || null,
      type: FRAUD_FLAG_TYPES.DISPUTE_BEFORE_PAYOUT,
      severity: 'high',
      description: `Dispute filed: ${disputeData.reason || 'Unknown reason'}`,
      status: 'active',
      createdAt: serverTimestamp(),
      resolvedAt: null,
      resolvedBy: null,
    });
  }

  // ── Helper Methods ────────────────────────────────────────────────────

  /**
   * Get recent payments for velocity checking.
   */
  async getRecentPayments(userId, hoursBack = 24) {
    const cutoff = new Date(Date.now() - hoursBack * 60 * 60 * 1000);
    const q = query(
      collection(this.firestore, 'invoices'),
      where('userId', '==', userId),
      where('status', '==', 'paid'),
      where('paidAt', '>=', cutoff),
      orderBy('paidAt', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Get all high-risk accounts (for admin dashboard).
   */
  async getHighRiskAccounts() {
    const q = query(
      collection(this.firestore, 'riskProfiles'),
      where('riskLevel', 'in', ['HIGH', 'MEDIUM']),
      orderBy('riskScore', 'desc'),
      limit(50)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Get accounts with active freezes (for admin dashboard).
   */
  async getFrozenAccounts() {
    const q = query(
      collection(this.firestore, 'riskProfiles'),
      where('accountFrozen', '==', true)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  /**
   * Get payout clearance summary for a user – used for balance display.
   */
  async getPayoutSummary(userId) {
    const clearances = await this.getPayoutClearances(userId);

    const pending = clearances.filter(c => c.status === CLEARANCE_STATUS.PENDING);
    const underReview = clearances.filter(c => c.status === CLEARANCE_STATUS.UNDER_REVIEW);
    const frozen = clearances.filter(c => c.status === CLEARANCE_STATUS.FROZEN);
    const cleared = clearances.filter(c => c.status === CLEARANCE_STATUS.CLEARED);

    const pendingAmount = pending.reduce((sum, c) => sum + (c.payoutAmount || 0), 0);
    const underReviewAmount = underReview.reduce((sum, c) => sum + (c.payoutAmount || 0), 0);
    const frozenAmount = frozen.reduce((sum, c) => sum + (c.payoutAmount || 0), 0);
    const reserveAmount = [...pending, ...underReview, ...frozen].reduce(
      (sum, c) => sum + (c.reserveAmount || 0), 0
    );

    // Find the next eligible payout date
    const nextEligible = pending
      .filter(c => c.eligibleAt)
      .sort((a, b) => {
        const aTime = a.eligibleAt.toDate ? a.eligibleAt.toDate().getTime() : a.eligibleAt;
        const bTime = b.eligibleAt.toDate ? b.eligibleAt.toDate().getTime() : b.eligibleAt;
        return aTime - bTime;
      })[0];

    return {
      pendingCount: pending.length,
      pendingAmount,
      underReviewCount: underReview.length,
      underReviewAmount,
      frozenCount: frozen.length,
      frozenAmount,
      clearedCount: cleared.length,
      reserveAmount,
      nextEligibleDate: nextEligible?.eligibleAt || null,
      totalHeld: pendingAmount + underReviewAmount + frozenAmount + reserveAmount,
    };
  }
}

export default FraudPreventionService;
